/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { lazy, Suspense, useCallback, useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '@/lib/brand/use-brand';
import { cn } from '@/lib/utils';
import { useStoreLocatorOptional } from '@/extensions/store-locator/providers/store-locator';
import type { StoreLocatorStore } from '@/extensions/store-locator/stores/store-locator-store';
import { HEADER_STORE_LINK_BY_BRAND } from './header-store-link-composition';

/*
    Deferred exactly as the PDP's Find In Store row defers it: the panel pulls
    in the locator form, the results list and the store-details renderer, none
    of which a shopper who never asks for a store should pay for.
*/
const StoreLocatorSheet = lazy(() => import('@/extensions/store-locator/components/header/store-locator-sheet'));

/*
    Module-scoped so their identity is stable across renders. An inline arrow
    would give `useSyncExternalStore` a new `getSnapshot` every render.
*/
const selectStoreName = (s: StoreLocatorStore) => s.selectedStoreInfo?.name;
const selectStoreId = (s: StoreLocatorStore) => s.selectedStoreInfo?.id;
const selectOpen = (s: StoreLocatorStore) => s.open;
const selectClose = (s: StoreLocatorStore) => s.close;
const selectIsOpen = (s: StoreLocatorStore) => s.isOpen;

/**
 * Live NM's header store control.
 *
 * Every measurement, both brands' evidence, and what was deliberately NOT
 * copied are in `header-store-link-composition.ts` beside
 * `HEADER_STORE_LINK_BY_BRAND`. This component only reads that map, so a brand
 * with a `null` arm renders nothing and BG is never named here.
 *
 * ── THE STATE IS SERVER-RESOLVED, WHICH IS THE WHOLE POINT ──
 *
 * This control was declined once, correctly, because live's is a CLIENT-SIDE
 * GEOLOCATION-DEPENDENT call: it can be delayed, it can fail, and it can
 * legitimately resolve to nothing. That reasoning does not apply to what is
 * rendered here, and the difference is mechanical rather than a judgement call.
 *
 * Ours reads `selectedStoreInfo`, which arrives from the ROOT LOADER: the
 * selected-store middleware parses a one-year `selectedStoreInfo` cookie into
 * router context, the root loader hands it to `StoreLocatorProvider`, and this
 * hook reads it out. That path contains no geolocation, no network call and no
 * client-side resolution step, so there is no delay to absorb, nothing to fail,
 * and no flash from one state to the other — the first painted frame is already
 * correct. The browser geolocation API is not merely unused: this app serves
 * `Permissions-Policy: geolocation=()`, so it could not run here even if
 * something asked it to.
 *
 * ── DEGRADING HONESTLY ──
 *
 * Live's control has exactly two states and its own bundle carries both as
 * `storeName || "Our Stores"`. Ours reproduces that fallback, and hardens it in
 * one place live does not have to worry about.
 *
 * The cookie is a client-writable string. It can carry an id with NO name — the
 * middleware only checks that an `id` is present — and the store normalizer
 * elsewhere defaults a missing name to the id itself. Rendering that would put
 * a raw store identifier in the header where a place name belongs, which is the
 * "broken name" case rather than a graceful one. So the name is used only when
 * it is present AND is not merely the id echoed back; anything else falls all
 * the way back to live's own default copy.
 *
 * WHAT THIS STILL CANNOT DO, stated plainly rather than papered over: a cookie
 * naming a store that has since been retired from the catalog will keep
 * rendering that name until the shopper opens the panel and picks another. The
 * header does not re-validate the id against Shopper Stores on every page load,
 * and it should not — that would reintroduce exactly the per-request lookup
 * whose delay-and-failure profile got this control declined the first time. The
 * name is stale, not wrong, and the panel behind it always shows the live list.
 *
 * ── WHY THE PANEL IS RENDERED FROM HERE ──
 *
 * `StoreLocatorSheet` is a controlled component and its trigger is its own
 * child, so the control has to own the open state. The shared store's
 * `open()` / `close()` are kept in step so that any consumer watching `isOpen`
 * still observes both edges.
 *
 * Once the panel has been opened once it STAYS in the tree, closed. That is
 * what makes the trigger a stable element across open and close, so the
 * dialog's own focus management returns focus to this button when it closes.
 */
export default function HeaderStoreLink(): ReactElement | null {
    const brand = useBrand();
    const { t } = useTranslation('extStoreLocator');
    const composition = HEADER_STORE_LINK_BY_BRAND[brand];

    const storeName = useStoreLocatorOptional(selectStoreName);
    const storeId = useStoreLocatorOptional(selectStoreId);
    const openStoreLocator = useStoreLocatorOptional(selectOpen);
    const closeStoreLocator = useStoreLocatorOptional(selectClose);
    const globalIsOpen = useStoreLocatorOptional(selectIsOpen);

    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [hasEverOpened, setHasEverOpened] = useState(false);

    /*
        THIS CONTROL IS THE ONLY MOUNT OF THE PANEL ON MOST ROUTES, so it has to
        answer `open()` calls that did not come from its own trigger.

        The comment above says the shared store's `open()`/`close()` are kept in
        step "so that any consumer watching `isOpen` still observes both edges".
        That was only ever true in one direction: this component WROTE the flag
        and never read it back, so a caller that set it — rather than clicking
        this button — got nothing. Measured on the cart: choosing the Pickup arm
        with no store selected calls `openStoreLocator()`
        (`extensions/bopis/components/delivery-options/cart-delivery-option.tsx`,
        `handleOpenStoreLocator`), the flag flipped, and no dialog appeared,
        which is why that arm read as dead.

        `store-locator-badge.tsx` already carries this same sync; the header
        control did not. Local state stays the source of truth for the sheet so
        focus return is unaffected — this only opens it.
    */
    useEffect(() => {
        if (globalIsOpen && !isPanelOpen) {
            setHasEverOpened(true);
            setIsPanelOpen(true);
        }
    }, [globalIsOpen, isPanelOpen]);

    const handleOpenChange = useCallback(
        (next: boolean) => {
            setIsPanelOpen(next);
            if (next) {
                setHasEverOpened(true);
                openStoreLocator?.();
            } else {
                closeStoreLocator?.();
            }
        },
        [openStoreLocator, closeStoreLocator]
    );

    const handleTriggerClick = useCallback(() => handleOpenChange(true), [handleOpenChange]);

    if (!composition) {
        return null;
    }

    /*
        Live's fallback, verbatim from its own bundle. A name equal to the id is
        the normalizer's "no name" sentinel, not a place name — see the comment
        above.
    */
    const defaultLabel = t('storeLocator.header.defaultLabel', { defaultValue: 'Our Stores' });
    const hasUsableName = Boolean(storeName) && storeName !== storeId;
    /*
        Live renders the LOCATION alone — "Denver", not "Neiman Marcus - Denver".
        The prefix to remove is the brand's, so it lives in the composition map
        beside the class string rather than here; a brand with no arm never
        reaches this line. `startsWith` rather than a split: eight of our 33 NM
        records carry a second separator, and splitting would drop the city.
        A name that does not start with the prefix passes through untouched.
    */
    const prefix = composition?.storeNamePrefix;
    const displayName =
        hasUsableName && prefix && (storeName as string).startsWith(prefix)
            ? (storeName as string).slice(prefix.length)
            : storeName;
    const label = hasUsableName ? (displayName as string) : defaultLabel;

    /*
        The element is the SAME node in both branches below — swapping it for a
        different one on close would drop the focus the dialog hands back.
        Live's is a `button` with no href, and its accessible name equals its
        visible text.
    */
    const trigger = (
        <button
            type="button"
            data-testid="header-store-link"
            className={cn(composition.className)}
            onClick={handleTriggerClick}>
            {label}
        </button>
    );

    if (!hasEverOpened) {
        return trigger;
    }

    return (
        <Suspense fallback={trigger}>
            <StoreLocatorSheet open={isPanelOpen} onOpenChange={handleOpenChange}>
                {trigger}
            </StoreLocatorSheet>
        </Suspense>
    );
}
